// ~/avaai_dashboard/frontend/src/context/PreferencesContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  getInitialPreferredLanguage,
  persistPreferredLanguage,
} from "../utils/avaaioLanguage";

interface Preferences {
  displayName?: string;
  theme?: string;
  accentColor?: string;
  language?: string;
}

interface PreferencesContextProps {
  preferences: Preferences;
  setPreferences: (prefs: Preferences) => void;
}

const PreferencesContext =
  createContext<PreferencesContextProps | undefined>(
    undefined
  );

export const PreferencesProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [preferences, setPreferences] =
    useState<Preferences>({
      theme: "dark",
      accentColor: "#0ff",
      language: getInitialPreferredLanguage(),
    });

  useEffect(() => {
    persistPreferredLanguage(
      preferences.language || "en"
    );
  }, [preferences.language]);

  useEffect(() => {
    function handleLanguageUpdate(
      incomingEvent: Event
    ) {
      const event = incomingEvent as CustomEvent<{
        language?: string;
      }>;

      const language = event.detail?.language;

      if (!language) {
        return;
      }

      setPreferences((current) => {
        if (current.language === language) {
          return current;
        }

        return {
          ...current,
          language,
        };
      });
    }

    window.addEventListener(
      "avaaio-language-updated",
      handleLanguageUpdate
    );

    return () => {
      window.removeEventListener(
        "avaaio-language-updated",
        handleLanguageUpdate
      );
    };
  }, []);

  return (
    <PreferencesContext.Provider
      value={{
        preferences,
        setPreferences,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = () => {
  const context = useContext(PreferencesContext);

  if (!context) {
    throw new Error(
      "usePreferences must be used within PreferencesProvider"
    );
  }

  return context;
};
